<div class="detail-page">

  <p-toast position="top-right" />
  <p-confirmDialog />

  <!-- Breadcrumb ──────────────────────────────────────────────────────────── -->
  <div class="breadcrumb">
    <a routerLink="/properties" class="breadcrumb-link">
      <i class="pi pi-angle-left"></i>Biens immobiliers
    </a>
    <i class="pi pi-chevron-right breadcrumb-sep"></i>
    <span class="breadcrumb-current">{{ property()?.title || 'Détail du bien' }}</span>
  </div>

  @if (loading()) {
  <div class="loading-wrap"><p-progressSpinner strokeWidth="4" styleClass="spinner-sm" /></div>
  } @else {

  <!-- En-tête ─────────────────────────────────────────────────────────── -->
  <div class="detail-header">
    <div class="detail-icon"><i class="pi pi-building"></i></div>
    <div class="detail-header-info">
      <h1 class="detail-title">
        {{ property()?.title || 'Bien #' + propertyId.slice(0, 8) }}
        @if (property()) {
          <app-status-tag [label]="propertyStatusLabel(property()!.status)" [severity]="propertyStatusSeverity(property()!.status)" />
        }
      </h1>
      <p class="detail-sub">
        {{ typeLabel(property()?.type ?? '') }} ·
        {{ property()?.transactionType === 'VENTE' ? 'Vente' : 'Location' }} ·
        {{ property()?.city }}{{ property()?.commune ? ', ' + property()?.commune : '' }}
        · {{ property()?.price | number }} FCFA
      </p>
    </div>
    <div class="detail-stats">
      <span class="stat-pill stat-validated">
        <i class="pi pi-check-circle"></i>
        {{ countByStatus('VALIDATED') }} validé{{ countByStatus('VALIDATED') > 1 ? 's' : '' }}
      </span>
      <span class="stat-pill stat-pending">
        <i class="pi pi-clock"></i>
        {{ countByStatus('PENDING') }} en attente
      </span>
      @if (countByStatus('REJECTED') > 0) {
      <span class="stat-pill stat-rejected">
        <i class="pi pi-times-circle"></i>
        {{ countByStatus('REJECTED') }} rejeté{{ countByStatus('REJECTED') > 1 ? 's' : '' }}
      </span>
      }
    </div>
  </div>

  <!-- Actions sur le bien ─────────────────────────────────────────────── -->
  @if (property(); as p) {
  @if (p.status === 'REJECTED' && p.rejectionReason) {
  <div class="rejection-banner">
    <i class="pi pi-exclamation-circle"></i>
    <span><strong>Motif du rejet :</strong> {{ p.rejectionReason }}</span>
  </div>
  }
  <div class="property-actions">
    @if (p.status === 'PENDING_REVIEW' && authService.hasPermission('PROPERTIES_VALIDATE')) {
    <button pButton icon="pi pi-check" label="Approuver" class="btn-validate"
            [loading]="propertyAction() === 'approve'" [disabled]="!!propertyAction()" (click)="approveProperty()"></button>
    <button pButton severity="danger" [outlined]="true" icon="pi pi-times" label="Rejeter"
            [disabled]="!!propertyAction()" (click)="openRejectProperty()"></button>
    }
    @if (authService.hasPermission('PROPERTIES_UPDATE')) {
    @for (a of statusActions(); track a.target) {
    <button pButton severity="secondary" [outlined]="true" [icon]="a.icon" [label]="a.label"
            [loading]="propertyAction() === a.target" [disabled]="!!propertyAction()" (click)="changeStatus(a)"></button>
    }
    <button pButton severity="secondary" [outlined]="true" icon="pi pi-pencil" label="Modifier"
            [disabled]="!!propertyAction()" (click)="editVisible.set(true)"></button>
    }
    @if (authService.hasPermission('PROPERTIES_DELETE')) {
    <button pButton severity="danger" [text]="true" icon="pi pi-trash" label="Supprimer" class="btn-delete"
            [loading]="propertyAction() === 'delete'" [disabled]="!!propertyAction()" (click)="confirmDelete($event)"></button>
    }
  </div>

  <app-property-edit-dialog [(visible)]="editVisible" [property]="p" (saved)="onEdited($event)" />

  <app-dialog [(visible)]="rejectPropVisible" header="Rejeter le bien" width="480px">
    <label class="reject-label" for="reject-prop"><i class="pi pi-exclamation-triangle"></i> Motif du rejet</label>
    <textarea pTextarea id="reject-prop" [(ngModel)]="rejectPropText" rows="4" class="reject-textarea"
              placeholder="Ce motif sera envoyé à l'annonceur..."></textarea>
    <div dialogFooter>
      <button pButton [text]="true" label="Annuler" (click)="rejectPropVisible.set(false)"></button>
      <button pButton severity="danger" icon="pi pi-send" label="Confirmer le rejet"
              [disabled]="!rejectPropText.trim() || propertyAction() === 'reject'"
              [loading]="propertyAction() === 'reject'" (click)="confirmRejectProperty()"></button>
    </div>
  </app-dialog>
  }

  <!-- Caractéristiques ────────────────────────────────────────────────── -->
  @if (property()) {
  <div class="property-info-card">
    <h2 class="section-title"><i class="pi pi-sliders-h"></i> Caractéristiques</h2>
    <div class="info-grid">
      @if (property()!.nbPieces) {
      <div class="info-item">
        <span class="info-label">Pièces</span>
        <span class="info-value">{{ property()!.nbPieces }}</span>
      </div>
      }
      @if (property()!.nbBedrooms != null) {
      <div class="info-item">
        <span class="info-label">Chambres</span>
        <span class="info-value">{{ property()!.nbBedrooms }}</span>
      </div>
      }
      @if (property()!.nbBathrooms != null) {
      <div class="info-item">
        <span class="info-label">Salles de bain</span>
        <span class="info-value">{{ property()!.nbBathrooms }}</span>
      </div>
      }
      @if (property()!.surface) {
      <div class="info-item">
        <span class="info-label">Surface</span>
        <span class="info-value">{{ property()!.surface }} m²</span>
      </div>
      }
      <div class="info-item">
        <span class="info-label">Meublé</span>
        <span class="info-value">{{ property()!.isFurnished ? 'Oui' : 'Non' }}</span>
      </div>
    </div>
    @if (property()!.features?.length) {
    <div class="features-row">
      @for (f of property()!.features!; track f) {
      <span class="feature-chip">{{ f }}</span>
      }
    </div>
    }
  </div>
  }

  <!-- Photos ──────────────────────────────────────────────────────────── -->
  <div class="section-block">
    <h2 class="section-title">
      <i class="pi pi-images"></i>
      Photos du bien
      @if (!loadingPhotos()) {
      <span class="section-count">{{ photoUrls().length }} photo{{ photoUrls().length > 1 ? 's' : '' }}</span>
      }
    </h2>

    @if (loadingPhotos()) {
    <div class="photos-loading">
      <p-progressSpinner strokeWidth="4" styleClass="spinner-xs" />
      <span>Chargement des photos…</span>
    </div>
    } @else if (photoUrls().length === 0) {
    <div class="photos-empty">
      <i class="pi pi-image"></i>
      <span>Aucune photo pour ce bien</span>
    </div>
    } @else {
    <div class="photos-grid">
      @for (url of photoUrls(); track url; let i = $index) {
      <div class="photo-card">
        <img [src]="url" [alt]="'Photo ' + (i + 1)" class="photo-img" (click)="openPhotoInTab(url)">
        <div class="photo-overlay" (click)="openPhotoInTab(url)">
          <i class="pi pi-arrow-up-right-and-arrow-down-left-from-center"></i>
        </div>
        <span class="photo-badge">{{ i + 1 }}</span>
      </div>
      }
    </div>
    }
  </div>

  <!-- Documents ───────────────────────────────────────────────────────── -->
  <div class="section-block">
    <h2 class="section-title">
      <i class="pi pi-folder-open"></i>
      Documents administratifs
      <span class="section-count">{{ documents().length }} document{{ documents().length > 1 ? 's' : '' }}</span>
    </h2>

    @if (documents().length === 0) {
    <app-empty-state size="sm" icon="pi-file" title="Aucun document trouvé"
                      message="Ce bien n'a pas encore de documents soumis." />
    } @else {
    <div class="docs-grid">
      @for (doc of documents(); track doc.id) {
      <div class="doc-card" [class.doc-card--validated]="doc.status === 'VALIDATED'"
        [class.doc-card--rejected]="doc.status === 'REJECTED'">

        <div class="doc-card-top">
          <div class="doc-icon-wrap">
            <i [class]="'pi ' + docIcon(doc.documentType)"></i>
          </div>
          <div class="doc-info">
            <p class="doc-type">{{ docTypeLabel(doc.documentType) }}</p>
            <span class="doc-id">ID : {{ doc.documentId.slice(0, 8) }}…</span>
          </div>
          <app-status-tag [label]="statusLabel(doc.status)" [severity]="statusSeverity(doc.status)" />
        </div>

        <div class="doc-actions">
          <button pButton [outlined]="true" (click)="openDoc(doc.id)"
                  icon="pi pi-external-link" label="Consulter" class="btn-doc-view" pTooltip="Ouvrir le document"></button>

          @if (doc.status !== 'VALIDATED' && authService.hasPermission('PROPERTIES_VALIDATE')) {
          <button pButton [disabled]="processing() === doc.id" [loading]="processing() === doc.id"
                  (click)="validate(doc)" icon="pi pi-check-circle" label="Valider" class="btn-validate"></button>
          }

          @if (doc.status !== 'REJECTED' && rejectId() !== doc.id && authService.hasPermission('PROPERTIES_VALIDATE')) {
          <button pButton severity="danger" [outlined]="true" (click)="startReject(doc.id)"
                  icon="pi pi-times-circle" label="Rejeter" class="btn-reject"></button>
          }
        </div>

        @if (rejectId() === doc.id) {
        <div class="reject-form">
          <div class="reject-label">
            <i class="pi pi-exclamation-triangle"></i>
            Motif du rejet
          </div>
          <textarea pTextarea [(ngModel)]="rejectText" rows="3" class="reject-textarea"
                    placeholder="Expliquez clairement pourquoi ce document est rejeté..."></textarea>
          <div class="reject-actions">
            <button pButton [text]="true" (click)="rejectId.set(null)" label="Annuler" class="btn-cancel-reject"></button>
            <button pButton severity="danger" [disabled]="!rejectText.trim() || processing() === doc.id"
                    [loading]="processing() === doc.id" (click)="confirmReject(doc)"
                    icon="pi pi-send" label="Confirmer le rejet" class="btn-confirm-reject"></button>
          </div>
        </div>
        }

      </div>
      }
    </div>
    }
  </div>

  }
</div>
